/**
 * Change-analysis enum labels (F7 / F8 / T4).
 *
 * Single source of truth for these literal values is docs/data-dictionary.md
 * (变更分析枚举) — mirrored from server/veridoc_changes/constants.py. The Chinese
 * labels match docs/glossary.md / the data dictionary so the UI never invents
 * its own wording. Do NOT hardcode these strings at call sites
 * (rules/common/coding-style.md).
 */
import type { ChangeType, ImportanceLevel } from '../../lib/veridoc-api';

/** F7 importance display labels (importance verdict). */
export const IMPORTANCE_LABELS: Record<ImportanceLevel, string> = {
  substantive: '实质性',
  wording: '措辞级',
  unclear: '待人工',
};

/**
 * Sort rank — most actionable first (substantive → unclear → wording). Mirrors
 * F6-6's severity ordering: the changes a reviewer must look at surface on top.
 */
export const IMPORTANCE_SEVERITY: Record<ImportanceLevel, number> = {
  substantive: 0,
  unclear: 1,
  wording: 2,
};

/** Change-type display labels (matches the diff views' wording). */
export const CHANGE_TYPE_LABELS: Record<ChangeType, string> = {
  addition: '新增',
  deletion: '删除',
  modification: '修改',
  move: '移动',
  format: '格式',
};

/**
 * Defensive: the importance annotation payload is `dict[str, Any]` on the wire,
 * so guard the value at the boundary. The backend already maps unknown labels to
 * `unclear`, but a hand-crafted / legacy row must not crash the view.
 */
export function normalizeImportance(value: unknown): ImportanceLevel {
  return value === 'substantive' || value === 'wording' || value === 'unclear'
    ? value
    : 'unclear';
}

/** Defensive change-type label lookup (falls back to a neutral 「变更」). */
export function changeTypeLabel(value: unknown): string {
  if (typeof value === 'string' && value in CHANGE_TYPE_LABELS) {
    return CHANGE_TYPE_LABELS[value as ChangeType];
  }
  return '变更';
}
